import React, { useState, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { QuestionMessage } from '../../types/protocol';

/** Node type display metadata — matches FlowPreview accent colors */
const NODE_TYPE_META: Record<string, { name: string; color: string }> = {
  Q: { name: 'Queue', color: '#ef5350' },
  S: { name: 'Schedule', color: '#ab47bc' },
  D: { name: 'Decision', color: '#42a5f5' },
  P: { name: 'Play', color: '#66bb6a' },
  G: { name: 'Gather', color: '#26a69a' },
  V: { name: 'Voice', color: '#00acc1' },
  M: { name: 'Speech', color: '#ab47bc' },
  R: { name: 'Destination', color: '#5c6bc0' },
  O: { name: 'Lead Create', color: '#ffa726' },
  J: { name: 'Jump', color: '#7e57c2' },
  DD: { name: 'Distributor', color: '#5c6bc0' },
  'source/embed': { name: 'Embed', color: '#4caf50' },
  'handler/form': { name: 'Guided Interaction', color: '#f4c542' },
  'handler/schedule': { name: 'Schedule', color: '#ab47bc' },
  'handler/split': { name: 'Split', color: '#42a5f5' },
  'target/integration': { name: 'Integration', color: '#d63333' },
};

interface NodeChild extends QuestionMessage {
  nodeId?: string;
  nodeLabel?: string;
  nodeType?: string;
}

interface NodeConfigFieldProps {
  field: string;
  children: NodeChild[];
  help?: string;
  onSubmit: (value: unknown) => void;
}

/** Searchable select dropdown for large option lists */
function SearchableSelect({
  options,
  value,
  onChange,
  placeholder,
}: {
  options: Array<{ value: string | number; label: string; description?: string }>;
  value: string | number | null;
  onChange: (val: string | number) => void;
  placeholder?: string;
}) {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState(false);

  const filtered = useMemo(() => {
    if (!search) return options;
    const q = search.toLowerCase();
    return options.filter(
      (o) => o.label.toLowerCase().includes(q) || (o.description ?? '').toLowerCase().includes(q),
    );
  }, [options, search]);

  const selectedLabel = useMemo(() => {
    const opt = options.find((o) => String(o.value) === String(value));
    return opt?.label ?? '';
  }, [options, value]);

  return (
    <div className="sp-searchable-select" data-open={open || undefined}>
      <button
        type="button"
        className="sp-searchable-select-trigger"
        onClick={() => setOpen(!open)}
        onBlur={(e) => {
          // Don't close if clicking inside the dropdown
          if (!e.currentTarget.parentElement?.contains(e.relatedTarget as Node)) {
            setTimeout(() => setOpen(false), 150);
          }
        }}
      >
        <span className={selectedLabel ? 'sp-searchable-select-value' : 'sp-searchable-select-placeholder'}>
          {selectedLabel || placeholder || 'Select...'}
        </span>
        <span className="sp-searchable-select-arrow">▾</span>
      </button>
      {open && (
        <div className="sp-searchable-select-dropdown">
          <input
            type="text"
            className="sp-searchable-select-search"
            placeholder="Search..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            autoFocus
            onClick={(e) => e.stopPropagation()}
          />
          <div className="sp-searchable-select-options">
            {filtered.length === 0 && <div className="sp-searchable-select-empty">{t('fields.noMatches')}</div>}
            {filtered.map((opt) => (
              <button
                key={opt.value}
                type="button"
                className={`sp-searchable-select-option${String(opt.value) === String(value) ? ' selected' : ''}`}
                onMouseDown={(e) => {
                  e.preventDefault();
                  onChange(opt.value);
                  setOpen(false);
                  setSearch('');
                }}
              >
                <span className="sp-searchable-select-option-label">{opt.label}</span>
                {opt.description && (
                  <span className="sp-searchable-select-option-desc">{opt.description}</span>
                )}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export function NodeConfigField({ field, children, help, onSubmit }: NodeConfigFieldProps): React.ReactElement {
  const { t } = useTranslation();

  // Group children by nodeId
  const nodeGroups = useMemo(() => {
    const groups: Array<{
      nodeId: string;
      nodeLabel: string;
      nodeType: string;
      fields: NodeChild[];
    }> = [];
    const seen = new Map<string, number>();

    for (const child of children) {
      const nodeId = child.nodeId ?? 'unknown';
      const idx = seen.get(nodeId);
      if (idx !== undefined && groups[idx]) {
        groups[idx].fields.push(child);
      } else {
        seen.set(nodeId, groups.length);
        groups.push({
          nodeId,
          nodeLabel: child.nodeLabel ?? nodeId,
          nodeType: child.nodeType ?? '?',
          fields: [child],
        });
      }
    }
    return groups;
  }, [children]);

  // Initialize values: key = "nodeId.configField"
  const [values, setValues] = useState<Record<string, unknown>>(() => {
    const init: Record<string, unknown> = {};
    for (const child of children) {
      const key = extractKey(child.field, field);
      init[key] = child.default ?? '';
    }
    return init;
  });

  const setValue = useCallback((childField: string, value: unknown) => {
    const key = extractKey(childField, field);
    setValues((prev) => ({ ...prev, [key]: value }));
  }, [field]);

  const handleSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      onSubmit(values);
    },
    [values, onSubmit],
  );

  return (
    <form className="sp-field sp-field-node-config" onSubmit={handleSubmit}>
      <div className="sp-node-config-cards">
        {nodeGroups.map((group) => {
          const meta = NODE_TYPE_META[group.nodeType];
          return (
            <div key={group.nodeId} className="sp-node-config-card">
              <div className="sp-node-config-card-header">
                <span
                  className="sp-node-config-type-badge"
                  style={{ backgroundColor: meta?.color ?? '#6B7280' }}
                >
                  {meta?.name ?? group.nodeType}
                </span>
                <span className="sp-node-config-node-label">{group.nodeLabel}</span>
              </div>
              <div className="sp-node-config-card-body">
                {group.fields.map((child) => {
                  const key = extractKey(child.field, field);
                  const value = values[key];

                  if ((child.fieldType === 'search' || child.fieldType === 'select_one') && child.options) {
                    return (
                      <label key={child.field} className="sp-node-config-field-row">
                        <span className="sp-node-config-field-label">{child.label}</span>
                        <SearchableSelect
                          options={child.options}
                          value={value as string | number | null}
                          onChange={(v) => setValue(child.field, v)}
                          placeholder={`Select ${(child.label ?? 'value').toLowerCase()}...`}
                        />
                      </label>
                    );
                  }

                  if (child.fieldType === 'number') {
                    return (
                      <label key={child.field} className="sp-node-config-field-row">
                        <span className="sp-node-config-field-label">{child.label}</span>
                        <input
                          type="number"
                          className="sp-field-input sp-node-config-number"
                          value={value as number}
                          onChange={(e) =>
                            setValue(child.field, e.target.value === '' ? '' : Number(e.target.value))
                          }
                        />
                      </label>
                    );
                  }

                  if (child.fieldType === 'text') {
                    return (
                      <label key={child.field} className="sp-node-config-field-row">
                        <span className="sp-node-config-field-label">{child.label}</span>
                        <input
                          type="text"
                          className="sp-field-input sp-node-config-text"
                          value={String(value ?? '')}
                          onChange={(e) => setValue(child.field, e.target.value)}
                          placeholder={child.help}
                        />
                      </label>
                    );
                  }

                  return null;
                })}
              </div>
            </div>
          );
        })}
      </div>
      {help && <span className="sp-field-help">{help}</span>}
      <button className="sp-field-submit" type="submit">
        {t('fields.submit')}
      </button>
    </form>
  );
}

/** Extract "nodeId.configField" from field paths like "node_config.n3.campaign" or "ivr.nodeConfig.n3.form.id". */
function extractKey(field: string, parentField: string): string {
  const prefix = `${parentField}.`;
  if (field.startsWith(prefix)) {
    return field.slice(prefix.length);
  }

  const parts = field.split('.');
  return parts.length >= 3 ? `${parts[parts.length - 2]}.${parts[parts.length - 1]}` : field;
}
